import React, { useState, useCallback } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

interface SingleFileUploadProps {
  onUpload: (
    file: File | null,
    columns: string[],
    data: any[][]
  ) => void;
}

const SingleFileUpload: React.FC<SingleFileUploadProps> = ({ onUpload }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
  });

  const parseCSV = (file: File): Promise<any[][]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: (result: Papa.ParseResult<any>) => resolve(result.data as any[][]),
        error: (error: Papa.ParseError) => reject(error),
      });
    });
  };

  const parseXLSX = (file: File): Promise<any[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        resolve(sheetData);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSubmit = async () => {
    if (!file) {
      alert('Please upload a spreadsheet.');
      return;
    }

    setLoading(true);

    try {
      let data: any[][] = [];
      let columns: string[] = [];

      if (file.name.endsWith('.csv')) {
        data = await parseCSV(file);
      } else {
        data = await parseXLSX(file);
      }

      columns = data[0] || [];
      data = data.slice(1);

      onUpload(file, columns, data);
    } catch (err) {
      console.error('Error loading spreadsheet:', err);
      alert('There was an error while reading the spreadsheet.');
    } finally {
      setLoading(false);
    }
  };

  const dropzoneStyle = {
    p: 2,
    border: '2px dashed #aaa',
    borderRadius: 2,
    textAlign: 'center' as const,
    color: '#555',
    cursor: 'pointer',
    mb: 2,
    backgroundColor: '#fafafa',
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        UPLOAD SPREADSHEET
      </Typography>

      {/* Dropzone for single file */}
      <Paper {...getRootProps()} sx={dropzoneStyle}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography>Drop the spreadsheet file here...</Typography>
        ) : file ? (
          <Typography>Selected File: {file.name}</Typography>
        ) : (
          <Typography>CLICK (or) DRAG YOUR FILE HERE</Typography>
        )}
      </Paper>

      <Button variant="contained" onClick={handleSubmit} disabled={loading}>
        {loading ? 'Uploading...' : 'UPLOAD & PREVIEW'}
      </Button>
    </Box>
  );
};

export default SingleFileUpload;